import type {
  EventModelConfig,
  EventStore,
  EventStoreConfig,
  ResourceModelConfig,
  ResourceStore,
  ResourceStoreConfig,
} from '@bryntum/scheduler';

/** A view change that only rescales the time axis re-renders the events already in the store. */
export function shouldLoadEventDataset(input: {
  initialised: boolean;
  groupingChanged: boolean;
  timeSpanAdvanced: boolean;
  eventsChanged: boolean;
}): boolean {
  return input.initialised && (
    input.groupingChanged ||
    (input.timeSpanAdvanced && input.eventsChanged)
  );
}

/** Whether a store sync has anything to diff into the event store. */
export function shouldSyncEventStore(input: {
  eventsChanged: boolean;
  whatIfChanged: boolean;
  resourcesChanged: boolean;
  timeSpanAdvanced: boolean;
  groupingChanged: boolean;
}): boolean {
  return input.eventsChanged
    || input.whatIfChanged
    || input.resourcesChanged
    || input.timeSpanAdvanced
    || input.groupingChanged;
}

/**
 * Whether a store sync pass changed anything the rendered rows are drawn from.
 * A pass that changed nothing has nothing to redraw, so it resumes rendering
 * without refreshing every rendered row.
 */
export function syncPassChangedScheduler(input: {
  timeSpanAdvanced: boolean;
  groupingChanged: boolean;
  resourcesApplied: boolean;
  eventStoreSynced: boolean;
  timeRangesChanged: boolean;
  globalTimeRangesChanged: boolean;
}): boolean {
  return input.timeSpanAdvanced
    || input.groupingChanged
    || input.resourcesApplied
    || input.eventStoreSynced
    || input.timeRangesChanged
    || input.globalTimeRangesChanged;
}

/** Whether an event field holds the same value, comparing dates by instant rather than by formatting them. */
export function sameEventValue(a: unknown, b: unknown): boolean {
  if (a instanceof Date && b instanceof Date) return Object.is(a.getTime(), b.getTime());
  return String(a) === String(b);
}

export function loadSchedulerEventDataset(
  store: Pick<EventStore, 'setConfig' | 'data'>,
  events: EventModelConfig[],
): void {
  store.setConfig({ syncDataOnLoad: false } satisfies Partial<EventStoreConfig>);
  store.data = events;
}

export interface ResourceTreeNodeConfig extends ResourceModelConfig {
  /** `true` marks a parent row whose children are not loaded. */
  children?: ResourceTreeNodeConfig[] | true;
}

/** New nodes to insert under a parent id — under the root when `parentId` is null. */
export interface ResourceTreeAddition {
  parentId: string | null;
  /** Id of the existing sibling to insert before; null appends at the end. */
  beforeId: string | null;
  nodes: ResourceTreeNodeConfig[];
}

export type ResourceTreeDelta =
  | { kind: 'unchanged' }
  /** The trees diverge, so only replacing the store data can express the change. */
  | { kind: 'replace' }
  | { kind: 'insert'; additions: ResourceTreeAddition[] };

/**
 * Keys that are not a row's own data. `expanded` is left out: the live row
 * already carries whether it is open, the user having toggled it there, and
 * replacing the data over a toggle rebuilds every row mid-animation — the rows
 * of the group come back unrendered. The tree store writes the row's position
 * (`parentId`, `parentIndex`, `orderedParentIndex`) onto the node configs it was
 * loaded with, so a group applied once would never match the fresh copy the next
 * page builds; the position itself is compared by the walk below.
 */
const NON_ROW_DATA_KEYS = new Set(['children', 'expanded', 'parentId', 'parentIndex', 'orderedParentIndex']);

function ownDataEqual(a: ResourceTreeNodeConfig, b: ResourceTreeNodeConfig): boolean {
  const own = (node: ResourceTreeNodeConfig): string[] =>
    Object.keys(node).filter(key => !NON_ROW_DATA_KEYS.has(key));
  const keysA = own(a);
  const keysB = own(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every(key => (a as Record<string, unknown>)[key] === (b as Record<string, unknown>)[key]);
}

/**
 * Whether `next` is `previous` with extra rows woven in, which is what a
 * resource page load produces: the new rows sort into the existing groups
 * rather than landing at the end. Replacing `store.data` for that case rebuilds
 * the whole tree, drops Bryntum's row cache and resets the vertical scroll
 * position; inserting only the new records keeps the rows already on screen.
 * Any reorder, removal or change to an existing row falls back to a replace.
 */
export function diffResourceTreeInsert(
  previous: readonly ResourceTreeNodeConfig[],
  next: readonly ResourceTreeNodeConfig[],
): ResourceTreeDelta {
  const additions: ResourceTreeAddition[] = [];

  const visit = (
    parentId: string | null,
    before: readonly ResourceTreeNodeConfig[],
    after: readonly ResourceTreeNodeConfig[],
  ): boolean => {
    const beforeById = new Map(before.map(node => [String(node.id), node]));
    let nextRetainedIndex = 0;
    let pending: ResourceTreeNodeConfig[] = [];

    const flush = (beforeId: string | null): void => {
      if (pending.length === 0) return;
      additions.push({ parentId, beforeId, nodes: pending });
      pending = [];
    };

    for (const node of after) {
      const id = String(node.id);
      const existing = beforeById.get(id);
      if (!existing) {
        pending.push(node);
        continue;
      }
      // Retained rows must keep their relative order, and their own data must
      // be untouched — a changed row cannot be expressed as an insertion.
      if (String(before[nextRetainedIndex]?.id) !== id) return false;
      nextRetainedIndex++;
      if (!ownDataEqual(existing, node)) return false;

      const childrenBefore = existing.children ?? [];
      const childrenAfter = node.children ?? [];
      // A group whose rows are loaded or released is a different row: its
      // children arrive or disappear wholesale, which no insertion covers.
      if (childrenBefore === true || childrenAfter === true) {
        if (childrenBefore !== childrenAfter) return false;
        flush(id);
        continue;
      }
      // A leaf turning into a parent (or back) changes the row itself.
      if ((childrenBefore.length === 0) !== (childrenAfter.length === 0)) return false;
      flush(id);
      if (!visit(id, childrenBefore, childrenAfter)) return false;
    }

    if (nextRetainedIndex !== before.length) return false;
    flush(null);
    return true;
  };

  if (!visit(null, previous, next)) return { kind: 'replace' };
  return additions.length === 0 ? { kind: 'unchanged' } : { kind: 'insert', additions };
}

interface ResourceTreeStore {
  rootNode: { insertChild(children: object[], before?: object): unknown };
  getById(id: string): { insertChild(children: object[], before?: object): unknown } | undefined;
}

/**
 * Insert the delta's new rows into the live tree. Returns false when a parent or
 * sibling the delta refers to is missing from the store, in which case the
 * caller must fall back to replacing the data.
 */
export function applyResourceTreeInsert(
  store: Pick<ResourceStore, 'rootNode' | 'getById'>,
  additions: readonly ResourceTreeAddition[],
): boolean {
  const treeStore = store as unknown as ResourceTreeStore;
  for (const { parentId, beforeId, nodes } of additions) {
    const parent = parentId == null ? treeStore.rootNode : treeStore.getById(parentId);
    if (!parent) return false;
    const before = beforeId == null ? undefined : treeStore.getById(beforeId);
    if (beforeId != null && !before) return false;
    parent.insertChild(nodes as object[], before);
  }
  return true;
}

export function syncSchedulerResources(
  store: Pick<ResourceStore, 'setConfig' | 'data'>,
  resources: ResourceModelConfig[],
): void {
  store.setConfig({
    syncDataOnLoad: { keepMissingValues: false },
  } satisfies Partial<ResourceStoreConfig>);
  store.data = resources;
}
